/**
 * createProgressPoller — shared polling loop for server-backed job progress.
 *
 * Contract (per approved UX/security rules):
 *  - Polls every `intervalMs` (default 2s) ONLY while the job is in a
 *    non-terminal state; stops immediately once terminal.
 *  - Throttles to `hiddenIntervalMs` (default 10s) while the browser tab
 *    is hidden; fires an immediate catch-up tick when it becomes visible.
 *  - Never resets the session idle timer — plain GET polling is ignored
 *    by the server-side idle policy (only the explicit heartbeat counts).
 *  - Treats 400/403/404 as terminal errors (expired/foreign/failed job);
 *    all other errors (network blips, 5xx, 429) are transient — the next
 *    tick retries, which is also how progress recovers after a temporary
 *    network interruption.
 *  - Hard ceiling `maxDurationMs` prevents forever-loops on a stuck job.
 *
 * fetchStatus must resolve to `{ terminal: boolean, data: any }`.
 * Returns `{ stop }` — idempotent; also removes the visibility listener.
 */
export const createProgressPoller = ({
  fetchStatus,
  onUpdate,
  onDone,
  onError,
  intervalMs = 2000,
  hiddenIntervalMs = 10000,
  maxDurationMs = 15 * 60 * 1000,
}) => {
  let stopped = false;
  let timer = null;
  const startedAt = Date.now();

  const cleanup = () => {
    stopped = true;
    if (timer) clearTimeout(timer);
    document.removeEventListener('visibilitychange', onVisibility);
  };

  const schedule = () => {
    if (stopped) return;
    const delay = document.hidden ? hiddenIntervalMs : intervalMs;
    timer = setTimeout(tick, delay);
  };

  const tick = async () => {
    if (stopped) return;
    if (Date.now() - startedAt > maxDurationMs) {
      cleanup();
      onError?.(new Error('Progress polling timed out'));
      return;
    }
    try {
      const { terminal, data } = await fetchStatus();
      if (stopped) return;
      onUpdate?.(data);
      if (terminal) {
        cleanup();
        onDone?.(data);
        return;
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status === 400 || status === 403 || status === 404) {
        cleanup();
        onError?.(err);
        return;
      }
      // Transient (network blip / 5xx / 429) — keep polling.
    }
    schedule();
  };

  const onVisibility = () => {
    if (!stopped && !document.hidden) {
      if (timer) clearTimeout(timer);
      tick();
    }
  };

  document.addEventListener('visibilitychange', onVisibility);
  tick();

  return { stop: cleanup };
};
